import { useMemo, useState } from 'react';
import type { FC } from 'react';

import { useIntl } from 'react-intl';

import { Button, Drawer } from 'antd';

import BoxContainer from '@src/components/BoxContainer';
import Title from '@src/components/BoxContainer/components/Title';
import FilterContainer from '@src/components/FilterContainer';
import type { IWorkJourneyMatrixCell } from '@src/models/interfaces/workJourneyManagement.interface';
import { DateUtil } from '@src/packages/utils/dates';
import IntlMessages from '@src/util/IntlMessages';

import { isReplacementCell } from '../../utils/isReplacementCell';
import { isAbsenceLikeMatrixExceptionCell } from '../../utils/isRemovableMatrixExceptionCell';
import drawerStyles from '../matrixSelectionDrawers.module.less';

export type WorkJourneyMatrixDayInfoDrawerProps = {
  open: boolean;
  date: string;
  userDisplayName: string;
  userIdCard?: string | null;
  cell: IWorkJourneyMatrixCell | null;
  onClose: () => void;
  onUndoAbsence: (
    exceptionId: string,
    cell?: IWorkJourneyMatrixCell | null,
  ) => Promise<void> | void;
  onUndoReplacement: (
    exceptionId: string,
    cell?: IWorkJourneyMatrixCell | null,
  ) => Promise<void> | void;
};

export const WorkJourneyMatrixDayInfoDrawer: FC<WorkJourneyMatrixDayInfoDrawerProps> = ({
  open,
  date,
  userDisplayName,
  userIdCard,
  cell,
  onClose,
  onUndoAbsence,
  onUndoReplacement,
}) => {
  const intl = useIntl();
  const [busy, setBusy] = useState<'absence' | 'replace' | null>(null);

  const formattedDate = useMemo(
    () =>
      DateUtil.formatDateWithIntl(date, intl, {
        day: '2-digit',
        month: '2-digit',
        year: '2-digit',
      }) || date,
    [date, intl],
  );

  const title = intl.formatMessage(
    { id: 'workJourney.management.matrix.infoDrawer.title' },
    { date: formattedDate, name: userDisplayName },
  );

  const showAbsence = isAbsenceLikeMatrixExceptionCell(cell);
  const showReplace = Boolean(cell && isReplacementCell(cell) && cell.exceptionId);
  const hasContent = showAbsence || showReplace;

  const handleUndoAbsence = async () => {
    if (!cell?.exceptionId) return;
    setBusy('absence');
    try {
      await onUndoAbsence(cell.exceptionId, cell);
    } finally {
      setBusy(null);
    }
  };

  const handleUndoReplace = async () => {
    if (!cell?.exceptionId) return;
    setBusy('replace');
    try {
      await onUndoReplacement(cell.exceptionId, cell);
    } finally {
      setBusy(null);
    }
  };

  return (
    <Drawer
      open={open}
      width={480}
      title={null}
      closable={false}
      footer={null}
      destroyOnClose
      onClose={onClose}
    >
      <BoxContainer>
        <BoxContainer content fixed bottomBorder>
          <FilterContainer goBack={onClose} title={<Title.Header value={title} />} />
        </BoxContainer>

        <BoxContainer content>
          {!hasContent && (
            <p className={drawerStyles.emptyInfo}>
              <IntlMessages id="workJourney.management.matrix.infoDrawer.empty" />
            </p>
          )}

          {showAbsence && (
            <div className={`${drawerStyles.alertCard} ${drawerStyles.alertCardAbsence}`}>
              <h3 className={drawerStyles.alertCardAbsenceTitle}>
                <IntlMessages id="workJourney.management.matrix.infoDrawer.absenceTitle" />
              </h3>
              <p className={drawerStyles.alertCardAbsenceBody}>
                {intl.formatMessage(
                  { id: 'workJourney.management.matrix.infoDrawer.absenceBody' },
                  {
                    name: userDisplayName,
                    idCard: userIdCard || '—',
                    reason: cell?.description || cell?.exceptionType || '—',
                  },
                )}
              </p>
              <Button type="primary" loading={busy === 'absence'} onClick={handleUndoAbsence}>
                <IntlMessages id="workJourney.management.matrix.infoDrawer.undoAbsence" />
              </Button>
            </div>
          )}

          {showReplace && (
            <div className={`${drawerStyles.alertCard} ${drawerStyles.alertCardReplace}`}>
              <h3 className={drawerStyles.alertCardReplaceTitle}>
                <IntlMessages id="workJourney.management.matrix.infoDrawer.replaceTitle" />
              </h3>
              <p className={drawerStyles.alertCardReplaceBody}>
                {intl.formatMessage(
                  { id: 'workJourney.management.matrix.infoDrawer.replaceBody' },
                  {
                    name:
                      cell?.replacementUserDisplayName ||
                      cell?.subjectDisplayName ||
                      '—',
                    idCard:
                      cell?.replacementUserIdCard || cell?.subjectIdCard || '—',
                    date: formattedDate,
                  },
                )}
              </p>
              <Button type="primary" loading={busy === 'replace'} onClick={handleUndoReplace}>
                <IntlMessages id="workJourney.management.matrix.infoDrawer.undoReplace" />
              </Button>
            </div>
          )}
        </BoxContainer>
      </BoxContainer>
    </Drawer>
  );
};
